import { useState } from "react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Mail, Phone, MapPin, CheckCircle2, Loader2, ArrowRight, ShieldCheck } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useSeo } from "@/hooks/useSeo";
import { COMPANY, VEHICLE_CATEGORIES, SERVICE_OPTIONS, CONTACT_METHODS } from "@/data/content";

const WEB3FORMS_ACCESS_KEY = "78e192ed-7bbc-44d7-85e6-2e398188c6cb";
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const EMPTY = {
  name: "", company: "", designation: "", email: "", phone: "",
  vehicle_category: "", service_required: "", project_details: "",
  preferred_contact_method: "Email", website: "",
};

export default function Consultation() {
  useSeo(
    "Book a Consultation | AutoDrive Tech Solutions",
    "Book a consultation with AutoDrive Tech Solutions to get a clear certification roadmap for your automotive product in India."
  );

  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (form.website) {
       return;
    }
    if (!form.name || !form.company || !form.email || !form.phone || !form.vehicle_category || !form.service_required || !form.project_details) {
      toast.error("Please fill in all required fields.");
      return;
    }
    if (!EMAIL_RE.test(form.email)) {
      toast.error("Please enter a valid business email address.");
      return;
    }
    setSubmitting(true);
  try {
  const response = await fetch("https://api.web3forms.com/submit", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
    body: JSON.stringify({
      access_key: WEB3FORMS_ACCESS_KEY,
      name: form.name,
      company: form.company,
      designation: form.designation,
      email: form.email,
      phone: form.phone,
      vehicle_category: form.vehicle_category,
      service_required: form.service_required,
      preferred_contact_method: form.preferred_contact_method,
      project_details: form.project_details,
      botcheck: form.website,
    }),
  });

  const result = await response.json();

  if (result.success) {
    setDone(true);
    setForm(EMPTY);
    toast.success("Your consultation request has been submitted.");
  } else {
    toast.error("We couldn't submit your request right now. Please try again.");
  }
} catch (err) {
  toast.error("We couldn't submit your request right now. Please try again.");
} finally {
  setSubmitting(false);
}
  };

  return (
    <section className="relative min-h-screen pt-32 pb-24 bg-[#F8FAFC] overflow-hidden" data-testid="consultation-page">
      <div className="absolute inset-0 bg-grid opacity-60" />
      <div className="absolute top-0 right-0 w-[500px] h-[500px] rounded-full bg-blue-100 blur-[160px] opacity-50" />

      <div className="relative max-w-[1200px] mx-auto px-5 sm:px-8 grid lg:grid-cols-[1fr_1.3fr] gap-12 lg:gap-16 items-start">
        {/* Left info */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="lg:sticky lg:top-28"
        >
          <div className="flex items-center gap-3 mb-4">
            <span className="h-px w-8 bg-[#1E40AF]" />
            <span className="text-xs font-semibold tracking-[0.25em] uppercase text-[#1E40AF]">Book a Consultation</span>
          </div>
          <h1 className="font-display font-bold text-4xl sm:text-5xl text-[#0F172A] leading-tight tracking-tight">
            Let's Plan Your Certification Journey
          </h1>
          <p className="mt-5 text-lg text-slate-600 leading-relaxed">
            Share a few details about your product and requirements. Our regulatory experts will get back to you
            with a clear, actionable roadmap.
          </p>

          <div className="mt-10 space-y-4">
            {[
              { icon: Mail, label: "Email", value: COMPANY.email, href: `mailto:${COMPANY.email}` },
              { icon: Phone, label: "Phone", value: COMPANY.phone, href: `tel:${COMPANY.phone.replace(/\s+/g, "")}` },
              { icon: MapPin, label: "Office", value: COMPANY.address },
            ].map((c) => (
              <div key={c.label} className="flex items-start gap-4 rounded-2xl border border-slate-200 bg-white p-5">
                <span className="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-blue-50 text-[#1E40AF] shrink-0">
                  <c.icon className="w-5 h-5" strokeWidth={1.7} />
                </span>
                <div>
                  <p className="text-xs font-semibold tracking-wide uppercase text-slate-400">{c.label}</p>
                  {c.href ? (
                    <a href={c.href} className="text-slate-800 font-medium hover:text-[#1E40AF] transition-colors break-all">{c.value}</a>
                  ) : (
                    <p className="text-slate-800 font-medium">{c.value}</p>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div className="mt-8 flex items-center gap-3 text-sm text-slate-500">
            <ShieldCheck className="w-5 h-5 text-[#16A34A]" />
            Your information is kept strictly confidential.
          </div>
        </motion.div>

        {/* Right form */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="rounded-3xl bg-white border border-slate-200 shadow-xl shadow-slate-900/5 p-6 sm:p-10"
        >
          {done ? (
            <div className="flex flex-col items-center text-center py-16" data-testid="consultation-success">
              <span className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-green-50 text-[#16A34A]">
                <CheckCircle2 className="w-10 h-10" />
              </span>
              <h2 className="mt-6 font-display font-bold text-2xl text-[#0F172A]">Request Received</h2>
              <p className="mt-3 text-slate-600 max-w-md">
                Thank you for contacting AutoDrive Tech Solutions. Your consultation request has been received
                successfully. Our team will review your requirement and get in touch with you shortly.
              </p>
              <button
                onClick={() => setDone(false)}
                data-testid="consultation-new-request"
                className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#1E40AF] hover:bg-[#2563EB] text-white font-semibold px-6 py-3 transition-colors"
              >
                Submit Another Request <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-6" data-testid="consultation-form">
              <div className="grid sm:grid-cols-2 gap-5">
                <Field label="Full Name" required>
                  <Input data-testid="input-name" value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Your name" className="h-12 rounded-xl" />
                </Field>
                <Field label="Company Name" required>
                  <Input data-testid="input-company" value={form.company} onChange={(e) => set("company", e.target.value)} placeholder="Company name" className="h-12 rounded-xl" />
                </Field>
                <Field label="Designation">
                  <Input data-testid="input-designation" value={form.designation} onChange={(e) => set("designation", e.target.value)} placeholder="Your role" className="h-12 rounded-xl" />
                </Field>
                <Field label="Business Email" required>
                  <Input data-testid="input-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="you@company.com" className="h-12 rounded-xl" />
                </Field>
                <Field label="Phone Number" required>
                  <Input data-testid="input-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="+91 ..." className="h-12 rounded-xl" />
                </Field>
                <Field label="Vehicle Category" required>
                  <Select value={form.vehicle_category} onValueChange={(v) => set("vehicle_category", v)}>
                    <SelectTrigger data-testid="select-vehicle-category" className="h-12 rounded-xl">
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      {VEHICLE_CATEGORIES.map((c) => (
                        <SelectItem key={c} value={c} data-testid={`vehicle-option-${c.toLowerCase().replace(/\s+/g, "-")}`}>{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
              </div>

              <div className="grid sm:grid-cols-2 gap-5">
                <Field label="Service Required" required>
                  <Select value={form.service_required} onValueChange={(v) => set("service_required", v)}>
                    <SelectTrigger data-testid="select-service" className="h-12 rounded-xl">
                      <SelectValue placeholder="Select a service" />
                    </SelectTrigger>
                    <SelectContent>
                      {SERVICE_OPTIONS.map((s) => (
                        <SelectItem key={s} value={s} data-testid={`service-option-${s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/-+$/g, "")}`}>{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Preferred Contact Method" required>
                  <Select value={form.preferred_contact_method} onValueChange={(v) => set("preferred_contact_method", v)}>
                    <SelectTrigger data-testid="select-contact-method" className="h-12 rounded-xl">
                      <SelectValue placeholder="Select method" />
                    </SelectTrigger>
                    <SelectContent>
                      {CONTACT_METHODS.map((m) => (
                        <SelectItem key={m} value={m} data-testid={`contact-option-${m.toLowerCase()}`}>{m}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
              </div>

              <Field label="Project / Requirement Details" required>
                <Textarea
                  data-testid="input-project-details"
                  value={form.project_details}
                  onChange={(e) => set("project_details", e.target.value)}
                  placeholder="Tell us about your product, target market and certification requirements..."
                  className="min-h-[140px] rounded-xl resize-none"
                />
              </Field>

              {/* Honeypot — hidden from users, catches bots */}
              <input
                type="text"
                name="website"
                tabIndex={-1}
                autoComplete="off"
                value={form.website}
                onChange={(e) => set("website", e.target.value)}
                className="hidden"
                aria-hidden="true"
              />

              <button
                type="submit"
                disabled={submitting}
                data-testid="consultation-submit"
                className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-[#1E40AF] hover:bg-[#2563EB] disabled:opacity-70 text-white font-semibold px-6 py-4 text-base transition-colors"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" /> Submitting...
                  </>
                ) : (
                  <>
                    Submit Request <ArrowRight className="w-5 h-5" />
                  </>
                )}
              </button>
            </form>
          )}
        </motion.div>
      </div>
    </section>
  );
}

const Field = ({ label, required, children }) => (
  <div className="space-y-2">
    <Label className="text-sm font-medium text-slate-700">
      {label} {required && <span className="text-[#1E40AF]">*</span>}
    </Label>
    {children}
  </div>
);
